import React from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";
import LinkWrapper from "@/components/linkWrapper";
import MarkdownWrapper from "@/components/markdownWrapper";

/*
  This component requires some changes to your config:
  
  ```
  // tailwind.config.js
  module.exports = {
    // ...
    plugins: [
      // ...
      require('@tailwindcss/typography'),
    ],
  }
  ```
*/

export const query = graphql`
  fragment ContentTwoColumnsData on StrapiComponentContentTwoColumns {
    title
    topic
    leftColBody {
      ...StrapiSharedBody
    }
    rightColBody {
      ...StrapiSharedBody
    }
    buttons {
      ...StrapiSharedLink
    }
  }
`;

function TwoColumns({ data }) {
  if (!data) {
    return <template />;
  }
  let highlightButton;
  let buttons;
  if (data.buttons) {
    buttons = [...data.buttons];
    highlightButton = buttons.shift();
  }
  return (
    <div className="overflow-hidden bg-background-white px-4 py-16 sm:px-6 lg:px-8 xl:py-36">
      <div className="mx-auto max-w-max lg:max-w-7xl">
        <div className="relative z-10 mb-8 md:mb-2 md:px-6">
          {(data.topic || data.title) && (
            <div className="max-w-prose text-base lg:max-w-none">
              {data.topic && (
                <h3 className="font-semibold uppercase leading-6 tracking-wide text-text-secondary">
                  <MarkdownWrapper text={data.topic} />
                </h3>
              )}
              {data.title && (
                <h2 className="mt-2 text-3xl font-extrabold leading-8 tracking-tight text-text-primary sm:text-4xl">
                  <MarkdownWrapper text={data.title} />
                </h2>
              )}
            </div>
          )}
        </div>
        <div className="relative">
          <svg
            className="absolute right-0 top-0 -mr-20 -mt-20 hidden md:block"
            width={404}
            height={384}
            fill="none"
            viewBox="0 0 404 384"
            aria-hidden="true"
          >
            <defs>
              <pattern
                id="95e8f2de-6d30-4b7e-8159-f791729db21b"
                x={0}
                y={0}
                width={20}
                height={20}
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x={0}
                  y={0}
                  width={4}
                  height={4}
                  className="text-text-light-gray"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width={404}
              height={384}
              fill="url(#95e8f2de-6d30-4b7e-8159-f791729db21b)"
            />
          </svg>
          <svg
            className="absolute bottom-0 left-0 -mb-20 -ml-20 hidden md:block"
            width={404}
            height={384}
            fill="none"
            viewBox="0 0 404 384"
            aria-hidden="true"
          >
            <defs>
              <pattern
                id="7a00fe67-0343-4a3c-8e81-c145097a3ce0"
                x={0}
                y={0}
                width={20}
                height={20}
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x={0}
                  y={0}
                  width={4}
                  height={4}
                  className="text-text-light-gray"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width={404}
              height={384}
              fill="url(#7a00fe67-0343-4a3c-8e81-c145097a3ce0)"
            />
          </svg>
          <div className="relative md:bg-background-white md:p-6">
            <div className="lg:grid lg:grid-cols-2 lg:gap-6">
              {data.leftColBody && data.leftColBody.text && (
                <div className="prose-prose-primary prose prose-lg text-text-secondary lg:max-w-none">
                  <MarkdownWrapper
                    text={data.leftColBody.text}
                    images={data.leftColBody.images}
                    imgDivClassName="w-full"
                    imgClassName="rounded-lg"
                    width={1310}
                    height={873}
                    replaceParagraph={false}
                  />
                </div>
              )}
              {data.rightColBody && data.rightColBody.text && (
                <div className="prose-prose-primary prose prose-lg mt-6 text-text-secondary lg:mt-0">
                  <MarkdownWrapper
                    text={data.rightColBody.text}
                    images={data.rightColBody.images}
                    imgDivClassName="w-full"
                    imgClassName="rounded-lg"
                    width={1310}
                    height={873}
                    replaceParagraph={false}
                  />
                </div>
              )}
            </div>
            {data.buttons && (
              <div className="mt-8 inline-flex">
                {highlightButton?.link?.url &&
                  (highlightButton?.overwriteTitle ||
                    highlightButton?.link?.title) && (
                    <div className="rounded-md shadow">
                      <LinkWrapper
                        to={highlightButton.link.url}
                        className="flex items-center justify-center rounded-md border border-transparent bg-background-primary px-5 py-3 text-base font-medium text-text-white hover:bg-background-primary-dark"
                      >
                        {highlightButton.overwriteTitle ||
                          highlightButton.link.title}
                      </LinkWrapper>
                    </div>
                  )}
                {buttons.map(
                  (button, index) =>
                    button.link?.url &&
                    (button.overwriteTitle || button.link?.title) && (
                      <div key={index} className="ml-4 rounded-md shadow">
                        <LinkWrapper
                          to={button.link.url}
                          className="flex items-center justify-center rounded-md border border-transparent bg-background-primary px-5 py-3 text-base font-medium text-text-white hover:bg-background-primary-dark"
                        >
                          {button.overwriteTitle || button.link.title}
                        </LinkWrapper>
                      </div>
                    )
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

TwoColumns.propTypes = {
  data: PropTypes.object,
};

export default TwoColumns;
